import axios from 'axios';
import Cookies from 'js-cookie';
import { API_URLS, TIMEOUTS } from '../config/apiConfig';
import { isVideoFormat } from '../config/outputFormatConfig';

// Base API configuration for merchant interface
const MERCHANT_API_BASE_URL = API_URLS.MERCHANT_BASE;


// Create axios instance for merchant API
const merchantAPI = axios.create({
  baseURL: MERCHANT_API_BASE_URL,
  timeout: TIMEOUTS.UPLOAD, // Longer timeout for file uploads and AI generation
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
merchantAPI.interceptors.request.use(
  (config) => {
    const token = Cookies.get('merchant_access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
merchantAPI.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response && error.response.status === 401) {
      // Only redirect if we're not already on the login page
      const currentPath = window.location.pathname;
      if (currentPath !== '/login') {
        // Token expired or invalid - clear auth data and redirect
        localStorage.removeItem('merchant_access_token');
        localStorage.removeItem('merchant');
        Cookies.remove('merchant_access_token');
        Cookies.remove('merchant');
        // Add a small delay to prevent redirect loops during component mounting
        setTimeout(() => {
          window.location.href = '/login';
        }, 100);
      }
    }
    if (error.response && error.response.status === 429) {
      let data = error.response.data || {};
      if (data instanceof Blob) {
        try { data = JSON.parse(await data.text()); } catch (_) { data = {}; }
      }
      if (data.error === 'UsageLimitExceeded') {
        window.dispatchEvent(new CustomEvent('usage_limit_exceeded', {
          detail: {
            message: data.message || 'You have reached your usage limit for this billing period.',
            limit: data.limit,
            used: data.used,
          },
        }));
      }
    }
    return Promise.reject(error);
  }
);

// Merchant Auth API
export const merchantAuthAPI = {
  register: (data) => merchantAPI.post('/auth/register', data),
  login: (credentials) => merchantAPI.post('/auth/login', credentials),
  logout: () => merchantAPI.post('/auth/logout'),
  verifyOTP: (data) => merchantAPI.post('/auth/verify-otp', data),
  resendOTP: (email) => merchantAPI.post('/auth/resend-otp', { email }),
  forgotPassword: (email) => merchantAPI.post('/auth/forgot-password', { email }),
  resetPassword: (data) => merchantAPI.post('/auth/reset-password', data),
  getProfile: () => merchantAPI.get('/auth/profile'),
  updateProfile: (data) => merchantAPI.put('/auth/profile', data),
  changePassword: (data) => merchantAPI.post('/auth/change-password', data),
  resendVerificationEmail: () => merchantAPI.post('/auth/resend-verification'),
  getNotificationSettings: () => merchantAPI.get('/auth/notification-settings'),
  updateNotificationSettings: (data) => merchantAPI.put('/auth/notification-settings', data),
  // Email availability check
  checkEmailAvailability: (email) => merchantAPI.post('/auth/check-email-availability', { email }),
  // GST availability check
  checkGSTAvailability: (gstNumber) => merchantAPI.post('/auth/check-gst-availability', { gst_number: gstNumber }),
  // Mobile availability check
  checkMobileAvailability: (mobile, mobileCode) => merchantAPI.post('/auth/check-mobile-availability', { mobile, mobile_code: mobileCode }),
  // Referral code validation
  checkReferralCode: (referralCode) => merchantAPI.post('/auth/check-referral-code', { referral_code: referralCode }),
  // Brand Logo API methods
  getBrandLogos: () => merchantAPI.get('/auth/brand-logos'),
  createBrandLogo: (data) => merchantAPI.post('/auth/brand-logos', data),
  updateBrandLogo: (logoId, data) => merchantAPI.put(`/auth/brand-logos/${logoId}`, data),
  deleteBrandLogo: (logoId) => merchantAPI.delete(`/auth/brand-logos/${logoId}`),
  setPrimaryBrandLogo: (logoId) => merchantAPI.put(`/auth/brand-logos/${logoId}/primary`),
  uploadBrandLogo: (formData) => merchantAPI.post('/auth/brand-logos/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  previewBrandLogo: (formData) => merchantAPI.post('/auth/brand-logos/preview', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  previewBrandLogoVariant: (formData) => merchantAPI.post('/auth/brand-logos/preview-variant', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  // Delete account
  deleteAccount: () => merchantAPI.post('/auth/delete-account'),
  // WhatsApp OTP methods
  sendWhatsAppOTP: (data) => merchantAPI.post('/auth/whatsapp-send-otp', data),
  verifyWhatsAppOTP: (data) => merchantAPI.post('/auth/whatsapp-verify-otp', data),
  checkWhatsAppUser: (data) => merchantAPI.post('/auth/check-whatsapp-user', data),
  completeProfile: (data) => merchantAPI.post('/auth/complete-profile', data),
  // Email Login methods
  sendEmailLoginOTP: (email) => merchantAPI.post('/auth/email-login-send-otp', { email }),
  verifyEmailLoginOTP: (data) => merchantAPI.post('/auth/email-login-verify-otp', data),
  // SMS OTP methods (disabled - kept for reference)
  sendSMSOTP: (data) => merchantAPI.post('/auth/sms-send-otp', data),
  verifySMSOTP: (data) => merchantAPI.post('/auth/sms-verify-otp', data),
  checkMobileUser: (data) => merchantAPI.post('/auth/check-mobile-user', data),
  // Unified Login OTP — triggers WhatsApp + Email at once (preferred over sendWhatsAppOTP + sendSMSOTP)
  sendLoginOTP: (data) => merchantAPI.post('/auth/send-login-otp', data),
  // Check if WhatsApp/SMS auth should be shown based on app version
  checkAuthVisibility: () => merchantAPI.get('/auth/check-auth-visibility'),
  // Profile edit contact verification
  sendProfileEmailOtp: (email) => merchantAPI.post('/auth/profile/verify-email/send-otp', { email }),
  verifyProfileEmailOtp: (email, otp) => merchantAPI.post('/auth/profile/verify-email/verify-otp', { email, otp }),
  sendProfileMobileOtp: (mobile, countryCode) => merchantAPI.post('/auth/profile/verify-mobile/send-otp', { mobile, country_code: countryCode }),
  verifyProfileMobileOtp: (mobile, countryCode, otp) => merchantAPI.post('/auth/profile/verify-mobile/verify-otp', { mobile, country_code: countryCode, otp }),
  recordConsent: (data) => merchantAPI.post('/auth/record-consent', data),
  // Detect country from IP (for default country code selection)
  detectCountry: () => merchantAPI.get('/auth/detect-country'),
  // Detect country code from phone number (from database)
  detectCountryFromPhone: (mobile) => merchantAPI.post('/auth/detect-country-from-phone', { mobile }),
};

// Merchant Dashboard API
export const merchantDashboardAPI = {
  getStats: () => merchantAPI.get('/dashboard/stats'),
  getCreditsInfo: () => merchantAPI.get('/dashboard/credits'),
  getRecentGenerations: (limit = 5) => merchantAPI.get(`/dashboard/recent-generations?limit=${limit}`),
  requestCredits: (packageId) => merchantAPI.post('/dashboard/request-credits', { package_id: packageId }),
  getActivityFeed: (params = {}) => merchantAPI.get('/dashboard/activity-feed', { params }),
};

// AI Generation API
export const aiGenerationAPI = {
  getOptions: (industryTypeId = null) => {
    const params = {};
    if (industryTypeId) {
      params.industry_type_id = industryTypeId;
    }
    return merchantAPI.get('/ai-generation/options', { params });
  },
  uploadImage: (formData) => {
    return merchantAPI.post('/ai-generation/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: TIMEOUTS.UPLOAD, // Use increased upload timeout
    });
  },
  uploadAIBrandLogo: (formData) => {
    return merchantAPI.post('/ai-generation/brand-logo/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: TIMEOUTS.UPLOAD, // Use increased upload timeout
    });
  },
  startGeneration: (data) => {
    // Use longer timeout for video generation and batch processing
    const isVideo = isVideoFormat(data.output_format_id);
    const isBatch = data.image_paths && data.image_paths.length > 1;

    let timeout = TIMEOUTS.DEFAULT;
    if (isVideo) {
      timeout = isBatch ? TIMEOUTS.VIDEO_GENERATION : TIMEOUTS.VIDEO_GENERATION;
    } else if (isBatch) {
      timeout = TIMEOUTS.BATCH_PROCESSING;
    }

    return axios.post(`${MERCHANT_API_BASE_URL}/ai-generation/generate`, data, {
      timeout: timeout,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${Cookies.get('merchant_access_token')}`,
      },
    });
  },
  startImageGeneration: (data) => {
    const isBatch = data.image_paths && data.image_paths.length > 1;
    // Use longer timeout for image generation to prevent timeout issues
    const timeout = isBatch ? TIMEOUTS.BATCH_PROCESSING : 300000; // 5 minutes for single image

    return merchantAPI.post('/ai-generation/generate', data, {
      timeout: timeout,
    });
  },
  startVideoGeneration: (data) => {
    const isBatch = data.image_paths && data.image_paths.length > 1;
    // Use longer timeout for video generation to prevent timeout issues
    const timeout = isBatch ? TIMEOUTS.VIDEO_GENERATION : 600000; // 10 minutes for single video

    return axios.post(`${MERCHANT_API_BASE_URL}/ai-generation/generate`, data, {
      timeout: timeout,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${Cookies.get('merchant_access_token')}`,
      },
    });
  },
  startBatchVideoGeneration: (data) => {
    return axios.post(`${MERCHANT_API_BASE_URL}/ai-generation/batch-video-generate`, data, {
      timeout: TIMEOUTS.VIDEO_GENERATION,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${Cookies.get('merchant_access_token')}`,
      },
    });
  },
  startBatchImageGeneration: (data) => {
    return axios.post(`${MERCHANT_API_BASE_URL}/ai-generation/batch-image-generate`, data, {
      timeout: TIMEOUTS.BATCH_PROCESSING,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${Cookies.get('merchant_access_token')}`,
      },
    });
  },
  getStatus: (generationId) => merchantAPI.get(`/ai-generation/status/${generationId}`),
  getBatchStatus: (generationIds) => merchantAPI.post('/ai-generation/batch-status', { generation_ids: generationIds }),
  getGenerationsByBatch: (batchId) => merchantAPI.get(`/ai-generation/batch/${batchId}`),
  getHistory: (params = {}) => merchantAPI.get('/ai-generation/history', { params }),
  downloadGeneration: (generationId) => {
    return merchantAPI.post(`/ai-generation/download/${generationId}`, {}, {
      responseType: 'blob',
    });
  },
  checkPreviewLimit: (generationId) =>
    merchantAPI.post(`/ai-generation/check-preview/${generationId}`),
  previewGeneration: (generationId) => {
    return merchantAPI.get(`/ai-generation/preview/${generationId}`, {
      responseType: 'blob',
    });
  },
  getBustColorsByFinish: (bustFinishId) => merchantAPI.get(`/ai-generation/bust-colors-by-finish/${bustFinishId}`),
  streamVideo: (generationId) => {
    return merchantAPI.get(`/ai-generation/video-stream/${generationId}`, {
      responseType: 'blob',
    });
  },

  // Gallery API
  getGallery: (params = {}) => {
    return merchantAPI.get('/ai-generation/gallery', { params });
  },

  /** Persist custom_tags on an existing generation (AI Attributes popup). */
  patchGenerationCustomTags: (generationId, body) =>
    merchantAPI.patch(`/ai-generation/${generationId}/custom-tags`, body),
  /** Fetch user-specific custom tag suggestions for live input hints. */
  getCustomTagSuggestions: (params = {}, config = {}) =>
    merchantAPI.get('/ai-generation/custom-tags/suggestions', { params, ...config }),
  /** Fetch unified search suggestions (tags + attributes). */
  getSearchSuggestions: (params = {}, config = {}) =>
    merchantAPI.get('/ai-generation/search/suggestions', { params, ...config }),


  downloadGalleryAsZip: (generationIds, config = {}) => {
    return merchantAPI.post('/ai-generation/gallery/download-zip', {
      generation_ids: generationIds
    }, {
      responseType: 'blob',
      ...config
    });
  },

  downloadGalleryAsZipMobile: (generationIds, config = {}) => {
    return merchantAPI.post('/ai-generation/gallery/download-zip', {
      generation_ids: generationIds,
      is_mobile: true
    }, {
      ...config
    });
  },

  // Server-side premium PDF catalogue generation.
  downloadGalleryAsPdf: (generationIds, config = {}) => {
    return merchantAPI.post('/ai-generation/gallery/download-pdf', {
      generation_ids: generationIds
    }, {
      responseType: 'blob',
      ...config
    });
  },

  getReelPrice: () => merchantAPI.get('/ai-generation/gallery/reel-price'),

  getReelMusicTracks: () => merchantAPI.get('/ai-generation/gallery/reel-music-tracks'),

  getReelMusicPreview: (trackId) =>
    merchantAPI.get(`/ai-generation/gallery/reel-music-tracks/${trackId}/preview`, {
      responseType: 'blob',
      timeout: 90000, // 90s — first call downloads the track (~8MB); subsequent calls are instant
    }),

  createReel: (generationIds, brandLogoId = null, musicTrackId = null, customMusicFile = null, config = {}) => {
    if (customMusicFile) {
      const form = new FormData();
      form.append('generation_ids', JSON.stringify(generationIds));
      if (brandLogoId) form.append('brand_logo_id', String(brandLogoId));
      form.append('music_file', customMusicFile);
      return merchantAPI.post('/ai-generation/gallery/create-reel', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 15000,
        ...config,
      });
    }
    return merchantAPI.post('/ai-generation/gallery/create-reel', {
      generation_ids: generationIds,
      brand_logo_id: brandLogoId,
      music_track_id: musicTrackId,
    }, {
      timeout: 15000,
      ...config,
    });
  },

  getReelStatus: (generationId) =>
    merchantAPI.get(`/ai-generation/gallery/reel-status/${generationId}`),

  cancelReel: (generationId) =>
    merchantAPI.delete(`/ai-generation/gallery/reel/${generationId}`),

  getReelVideoUrl: (generationId) =>
    merchantAPI.get(`/ai-generation/gallery/reel-video-url/${generationId}`),

  getReelVideo: (generationId) =>
    merchantAPI.get(`/ai-generation/video-stream/${generationId}`, {
      responseType: 'blob',
      timeout: 300000,
    }),

  submitFeedback: (generationId, feedback) => {
    return merchantAPI.post(`/ai-generation/feedback/${generationId}`, feedback);
  },
  cancelGeneration: (generationId) => {
    return merchantAPI.post(`/ai-generation/cancel/${generationId}`);
  },
  cancelMultipleGenerations: (generationIds) => {
    return merchantAPI.post('/ai-generation/cancel-multiple', { generation_ids: generationIds });
  },
  getCreditSettings: () => merchantAPI.get('/ai-generation/credit-settings'),
  calculateCredits: (data) => merchantAPI.post('/ai-generation/calculate-credits', data),
  triggerGeneration: (generationId) => {
    // Use longer timeout for video generation trigger
    return axios.post(`${MERCHANT_API_BASE_URL}/ai-generation/trigger-generation/${generationId}`, {}, {
      timeout: TIMEOUTS.VIDEO_GENERATION,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${Cookies.get('merchant_access_token')}`,
      },
    });
  },
  checkStaleProcessing: () => {
    // Check for processing generations older than 2 hours and mark them as failed
    return merchantAPI.post('/ai-generation/check-stale-processing');
  },
};

// Merchant Model Face API (AI Model Studio)
export const merchantModelFaceAPI = {
  listModels: (params = {}) => merchantAPI.get('/model-face/models', { params }),
  listAssignedModels: (params = {}) => merchantAPI.get('/model-face/assigned-models', { params }),
  /** Credit-based booking (replaces legacy purchase-requests). */
  bookModels: (data) => merchantAPI.post('/model-face/book-models', data),
  getModelStudioCreditBalance: () => merchantAPI.get('/model-face/credits-balance'),
};

// Credits Management API
export const creditsAPI = {
  getCreditPackages: () => merchantAPI.get('/dashboard/credits/packages'),
  purchaseCredits: (data) => merchantAPI.post('/dashboard/credits/purchase', data),
  getCreditTransactions: (params = {}) => merchantAPI.get('/dashboard/credits/transactions', { params }),
  getTransactionTypes: () => merchantAPI.get('/dashboard/credits/transaction-types'),
  requestCustomCredits: (data) => merchantAPI.post('/dashboard/credits/request-custom', data),
  getBalance: () => merchantAPI.get('/dashboard/credits'),
  getCreditRequests: (params = {}) => merchantAPI.get('/dashboard/credits/requests', { params }),
  getSystemSettings: () => merchantAPI.get('/master-data/system-settings/public'),
};

// Referral Code API (for merchant interface)
export const referralCodeAPI = {
  // Get merchant's own referral code
  getMyReferralCode: () => merchantAPI.get('/dashboard/referral-code'),

  // Validate a referral code during registration
  validateReferralCode: (code) => merchantAPI.post('/dashboard/referral-code/validate', { code }),

  // Get referral code statistics for merchant
  getStats: () => merchantAPI.get('/referral-codes/stats'),

  // Get referral history
  getReferralHistory: (params = {}) => merchantAPI.get('/referral-codes/history', { params }),
};

// Merchant Permissions API
export const merchantPermissionsAPI = {
  getPermissions: () => merchantAPI.get('/permissions/permissions'),
  getModules: () => merchantAPI.get('/permissions/modules'),
  getModuleActions: (moduleId) => merchantAPI.get(`/permissions/module/${moduleId}/actions`),
  checkPermission: (moduleName, actionName) => merchantAPI.post('/permissions/check', {
    module_name: moduleName,
    action_name: actionName
  }),
  getPermissionsSummary: () => merchantAPI.get('/permissions/summary'),
  getUserPermissions: () => merchantAPI.get('/permissions/user-permissions'),
};

// Admin API for master data (used in admin panel)
const ADMIN_API_BASE_URL = API_URLS.ADMIN_BASE;

const adminAPI = axios.create({
  baseURL: ADMIN_API_BASE_URL,
  timeout: TIMEOUTS.DEFAULT,
});

// Admin auth interceptor
adminAPI.interceptors.request.use(
  (config) => {
    const token = Cookies.get('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Master Data API (for admin panel)
export const masterDataAPI = {
  // Generic CRUD for all master data types
  getAll: (modelName, params = {}) => adminAPI.get(`/master-data/${modelName}`, { params }),
  getById: (modelName, id) => adminAPI.get(`/master-data/${modelName}/${id}`),
  create: (modelName, data) => adminAPI.post(`/master-data/${modelName}`, data),
  update: (modelName, id, data) => adminAPI.put(`/master-data/${modelName}/${id}`, data),
  delete: (modelName, id) => adminAPI.delete(`/master-data/${modelName}/${id}`),
  reorder: (modelName, order) => adminAPI.post(`/master-data/${modelName}/reorder`, { order }),
  bulkActivate: (data) => adminAPI.post('/master-data/bulk-activate', data),

  // System Settings
  getSettings: () => adminAPI.get('/master-data/settings'),
  updateSetting: (key, value) => adminAPI.put(`/master-data/settings/${key}`, { value }),

  // Initialize master data
  initialize: () => adminAPI.post('/master-data/initialize'),
};

// API Partners API (for admin panel)
export const apiPartnersAPI = {
  getAll: (params = {}) => adminAPI.get('/api-partners/', { params }),
  getById: (id) => adminAPI.get(`/api-partners/${id}`),
  create: (data) => adminAPI.post('/api-partners/', data),
  update: (id, data) => adminAPI.put(`/api-partners/${id}`, data),
  suspend: (id, reason) => adminAPI.post(`/api-partners/${id}/suspend`, { reason }),
  activate: (id) => adminAPI.post(`/api-partners/${id}/activate`),
  regenerateCredentials: (id) => adminAPI.post(`/api-partners/${id}/regenerate-credentials`),
  getUsageStats: (id, params = {}) => adminAPI.get(`/api-partners/${id}/usage-stats`, { params }),
  getRevenueSummary: () => adminAPI.get('/api-partners/revenue-summary'),
};

// Merchants Management API (for admin panel)
export const merchantsAdminAPI = {
  getAll: (params = {}) => adminAPI.get('/merchants/', { params }),
  getById: (id) => adminAPI.get(`/merchants/${id}`),
  suspend: (id, reason) => adminAPI.post(`/merchants/${id}/suspend`, { reason }),
  activate: (id) => adminAPI.post(`/merchants/${id}/activate`),
  reactivate: (id) => adminAPI.post(`/merchants/${id}/reactivate`),
  addCredits: (id, data) => adminAPI.post(`/merchants/${id}/credits`, data),
  getGenerations: (id, params = {}) => adminAPI.get(`/merchants/${id}/generations`, { params }),
  exportData: () => adminAPI.get('/merchants/export', { responseType: 'blob' }),
};

// Admin Auth API (for admin panel)
export const adminAuthAPI = {
  login: (credentials) => adminAPI.post('/auth/login', credentials),
  forgotPassword: (email) => adminAPI.post('/auth/forgot-password', { email }),
  resetPassword: (data) => adminAPI.post('/auth/reset-password', data),
  getProfile: () => adminAPI.get('/auth/profile'),
};

// ---------------------------------------------------------------------------
// Catalog Management API — merchant (authenticated)
// All requests go through merchantAPI (JWT attached via interceptor).
// getCatalogItems uses AbortController for request deduplication —
// pass { signal } to cancel in-flight requests when cursor changes rapidly.
// ---------------------------------------------------------------------------
export const catalogAPI = {
  getCatalogs:     (params)              => merchantAPI.get('/catalogs/', { params }),
  createCatalog:   (data)                => merchantAPI.post('/catalogs/', data),
  getCatalog:      (id)                  => merchantAPI.get(`/catalogs/${id}`),
  updateCatalog:   (id, data)            => merchantAPI.put(`/catalogs/${id}`, data),
  deleteCatalog:   (id)                  => merchantAPI.delete(`/catalogs/${id}`),
  getCatalogItems: (id, params, signal)  => merchantAPI.get(`/catalogs/${id}/items`, { params, signal }),
  addItems:        (id, generationIds)   => merchantAPI.post(`/catalogs/${id}/items`, { generation_ids: generationIds }),
  updateItem:      (id, itemId, data)    => merchantAPI.put(`/catalogs/${id}/items/${itemId}`, data),
  removeItem:      (id, itemId)          => merchantAPI.delete(`/catalogs/${id}/items/${itemId}`),
  getShareLinks:   (id)                  => merchantAPI.get(`/catalogs/${id}/share-links`),
  createShareLink: (id, data)            => merchantAPI.post(`/catalogs/${id}/share-links`, data),
  updateShareLink: (linkId, data)        => merchantAPI.patch(`/catalogs/share-links/${linkId}`, data),
  deleteShareLink: (linkId)              => merchantAPI.delete(`/catalogs/share-links/${linkId}`),
};

// ---------------------------------------------------------------------------
// Public Catalog API — no authentication, raw axios instance
// Used by CatalogViewer at /catalog/:token (outside any auth guard).
// ---------------------------------------------------------------------------
const publicCatalogAxios = axios.create({
  baseURL: API_URLS.BACKEND_BASE,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
});

/** Backend proxy stream URL — no presigned S3 URL in the browser. */
export const publicCatalogStreamUrl = (token, itemId, kind = 'thumb') => {
  const { API_URLS } = require('../config/apiConfig');
  return `${API_URLS.BACKEND_BASE}/api/public/catalog/${token}/stream/${itemId}?kind=${kind}`;
};

export const publicCatalogAPI = {
  getCatalog:  (token)         => publicCatalogAxios.get(`/api/public/catalog/${token}`),
  getMediaUrl: (token, itemId) => publicCatalogAxios.get(`/api/public/catalog/${token}/media/${itemId}`),
  streamUrl:   publicCatalogStreamUrl,
};

// Website/Public API
export const websiteAPI = {
  contactUs: (data) => {
    const baseURL = API_URLS.BACKEND_BASE;
    return axios.post(`${baseURL}/api/website/contact-us`, data, {
      timeout: TIMEOUTS.DEFAULT,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  },
  getRecaptchaConfig: () => {
    const baseURL = API_URLS.BACKEND_BASE;
    return axios.get(`${baseURL}/api/website/recaptcha-config`, { timeout: TIMEOUTS.DEFAULT });
  },
};

const backendBase = axios.create({
  baseURL: API_URLS.BACKEND_BASE,
  timeout: TIMEOUTS.DEFAULT,
  headers: { 'Content-Type': 'application/json' },
});

export const locationAPI = {
  getCountries:       ()   => backendBase.get('/api/admin/master-data/countries'),
  getStatesByCountry: (id) => backendBase.get(`/api/admin/master-data/states/${id}`),
  getCitiesByState:   (id) => backendBase.get(`/api/admin/master-data/cities/${id}`),
};

// Main merchant API object with all methods for easy access
const merchantAPIMain = {
  // Auth methods
  ...merchantAuthAPI,

  // Dashboard methods
  ...merchantDashboardAPI,

  // AI Generation methods
  ...aiGenerationAPI,

  // Model Face purchase methods
  ...merchantModelFaceAPI,

  // Credits methods
  ...creditsAPI,

  // Referral Code methods
  ...referralCodeAPI,

  // Website/Public methods
  ...websiteAPI,

  // Location (country/state/city) methods
  ...locationAPI,

  // Convenience methods for backward compatibility
  getCreditPackages: creditsAPI.getCreditPackages,
  purchaseCredits: creditsAPI.purchaseCredits,
  getCreditTransactions: creditsAPI.getCreditTransactions,
  requestCustomCredits: creditsAPI.requestCustomCredits,
  getCreditRequests: creditsAPI.getCreditRequests,
};

export default merchantAPIMain; 
